import dayjs from 'dayjs'
import { InspectionStatus } from 'features/inbox/types/types'
import {
  GeckoComponentDataset,
  GeckoInspection,
  GeckoPublishingState,
} from 'types'

const THREE_MONTHS_AGO = dayjs().subtract(3, 'months')

/**
 * Computes the derived status for an inspection based on its datasets and date.
 *
 * Status determination logic:
 * - `completed`: Has published datasets with finalized publishing state
 * - `preliminary_results_available`: Has published datasets but not finalized
 * - `closed`: No published datasets and inspection is older than 4 months
 * - `in_progress`: No published datasets but has unpublished datasets
 * - `scheduled`: No datasets at all
 */
export const computeInspectionStatus = (
  inspection: GeckoInspection,
  datasets: ReadonlyArray<GeckoComponentDataset> = []
): InspectionStatus => {
  // Check if CDSes exist and have access_restriction: none (published)
  const publishedDatasets = datasets.filter(
    (dataset) => dataset.access_restriction === null
  )

  if (publishedDatasets.length > 0) {
    // CDSes exist and are published - check if any are finalized
    const hasFinalized = publishedDatasets.some(
      (dataset) =>
        (dataset.data.publishing_state as GeckoPublishingState).id ===
        GeckoPublishingState.Finalized.id
    )

    return hasFinalized ? 'completed' : 'preliminary_results_available'
  }

  // No published CDSes
  const inspectionDate = dayjs(inspection.data.date)
  const isOlderThanThreeMonths = inspectionDate.isBefore(THREE_MONTHS_AGO)

  if (isOlderThanThreeMonths) {
    // Old inspection with no published CDSes
    return 'closed'
  }

  if (datasets.length > 0) {
    // CDSes exist but not published - in progress
    return 'in_progress'
  }

  // No CDSes at all and not old - scheduled
  return 'scheduled'
}
